import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft,
  CreditCard,
  Check,
  Zap,
  Clock,
  Shield,
  ChevronRight,
  AlertCircle,
  Sparkles
} from 'lucide-react';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_825c5321-5d95-4d75-842e-1e4ffe27dc80/artifacts/nz2oqlak_image.png';

const plans = [
  {
    name: 'Essentials',
    price: { monthly: 25, annual: 20 },
    description: 'For small teams',
    features: [
      '1 user',
      '250 RFQs/year',
      'CRM & RFQ',
      'Basic BOM',
      'Email support',
    ],
    popular: false,
  },
  {
    name: 'Professional',
    price: { monthly: 100, annual: 80 },
    description: 'For growing teams',
    features: [
      '5 users',
      '1,000 RFQs/year',
      'BOM versioning',
      'Workflow & approvals',
      'Analytics',
      'Priority support',
    ],
    popular: true,
  },
  {
    name: 'Enterprise',
    price: { monthly: null, annual: null },
    description: 'For large organizations',
    features: [
      'Unlimited users',
      'Unlimited RFQs',
      'Full customization',
      'Dedicated manager',
      'SLA guarantee',
    ],
    popular: false,
  },
];

export const BillingPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [selectedPlan, setSelectedPlan] = useState('Professional');
  const [trialDaysLeft, setTrialDaysLeft] = useState(30);

  useEffect(() => {
    const userData = localStorage.getItem('costgenie_user');
    if (userData) {
      const parsedUser = JSON.parse(userData);
      setUser(parsedUser);
      
      if (parsedUser.trialEndsAt) {
        const daysLeft = Math.ceil((new Date(parsedUser.trialEndsAt) - new Date()) / (1000 * 60 * 60 * 24));
        setTrialDaysLeft(Math.max(0, daysLeft));
      }
    } else {
      navigate('/login');
    }
  }, [navigate]);

  const handleUpgrade = (planName) => {
    setSelectedPlan(planName);
    if (planName === 'Enterprise') {
      // navigate('/demo');
      console.log('Enterprise plan selected');
    } else {
      navigate(`/subscribe?plan=${planName.toLowerCase()}&billing=${billingCycle}`);
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-[#030810]" data-testid="billing-page">
      {/* Background Effects */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-[-20%] right-[-10%] w-[50vw] h-[50vw] rounded-full bg-cyan-500/5 blur-[100px]" />
        <div className="absolute bottom-[-20%] left-[-10%] w-[40vw] h-[40vw] rounded-full bg-purple-500/5 blur-[80px]" />
      </div>

      <div className="relative z-10">
        {/* Header */}
        <header className="border-b border-white/5">
          <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link 
                to="/dashboard" 
                className="p-2 text-white/60 hover:text-white transition-colors rounded-lg hover:bg-white/5"
              >
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <Link to="/">
                <img src={LOGO_URL} alt="CostGenie" className="h-8 w-auto" />
              </Link>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center text-white text-sm font-semibold">
                {user.name?.charAt(0).toUpperCase() || 'U'}
              </div>
            </div>
          </div>
        </header>

        {/* Content */}
        <main className="max-w-6xl mx-auto px-6 py-12">
          {/* Trial Banner */}
          {user.plan === 'Trial' && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-8 p-4 rounded-xl bg-gradient-to-r from-amber-500/10 to-transparent border border-amber-500/20 flex items-center gap-4"
              data-testid="trial-banner"
            >
              <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                <Clock className="w-5 h-5 text-amber-400" />
              </div>
              <div className="flex-1">
                <p className="text-white/90 font-medium">
                  Your free trial ends in {trialDaysLeft} days
                </p>
                <p className="text-white/50 text-sm">
                  Upgrade now to keep access to all features and your data
                </p>
              </div>
            </motion.div>
          )}

          {/* Page Header */}
          <div className="text-center mb-12">
            <h1 className="text-3xl font-semibold mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
              Choose Your Plan
            </h1>
            <p className="text-white/50 max-w-xl mx-auto">
              Select the plan that best fits your manufacturing operations. 
              All plans include a 30-day money-back guarantee.
            </p>

            {/* Billing Toggle */}
            <div className="mt-8 inline-flex items-center gap-3 p-1 rounded-full bg-white/5 border border-white/10">
              <button
                onClick={() => setBillingCycle('monthly')}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
                  billingCycle === 'monthly'
                    ? 'bg-cyan-500 text-[#030810]'
                    : 'text-white/60 hover:text-white'
                }`}
                data-testid="billing-monthly"
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingCycle('annual')}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${
                  billingCycle === 'annual'
                    ? 'bg-cyan-500 text-[#030810]'
                    : 'text-white/60 hover:text-white'
                }`}
                data-testid="billing-annual"
              >
                Annual
                <span className={`text-xs px-2 py-0.5 rounded-full ${
                  billingCycle === 'annual' 
                    ? 'bg-[#030810]/20 text-[#030810]' 
                    : 'bg-green-500/20 text-green-400'
                }`}>
                  Save 20%
                </span>
              </button>
            </div>
          </div>

          {/* Plans Grid */}
          <div className="grid md:grid-cols-3 gap-6">
            {plans.map((plan, index) => (
              <motion.div
                key={plan.name}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`relative rounded-2xl p-6 ${
                  plan.popular
                    ? 'bg-gradient-to-b from-cyan-500/10 to-transparent border-2 border-cyan-500/30'
                    : 'bg-white/2 border border-white/5'
                }`}
                data-testid={`plan-${plan.name.toLowerCase()}`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="px-4 py-1 rounded-full text-xs font-medium bg-cyan-500 text-[#030810]">
                      Most Popular
                    </span>
                  </div>
                )}

                <div className="mb-6">
                  <h3 className="text-xl font-semibold mb-1">{plan.name}</h3>
                  <p className="text-white/40 text-sm">{plan.description}</p>
                </div>

                <div className="mb-6">
                  {plan.price.monthly ? (
                    <>
                      <span className="text-4xl font-bold">
                        ${billingCycle === 'monthly' ? plan.price.monthly : plan.price.annual}
                      </span>
                      <span className="text-white/40 text-sm">/month</span>
                      {billingCycle === 'annual' && (
                        <p className="text-green-400 text-xs mt-1">
                          Billed annually (${plan.price.annual * 12}/year)
                        </p>
                      )}
                    </>
                  ) : (
                    <span className="text-2xl font-semibold text-white/80">Custom pricing</span>
                  )}
                </div>

                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex items-center gap-3 text-sm text-white/70">
                      <Check className="w-4 h-4 text-cyan-400 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>

                {plan.name === 'Enterprise' ? (
                  /* <button
                    onClick={() => handleUpgrade(plan.name)}
                    className={`w-full py-3 rounded-lg font-medium text-sm transition-all ${
                      plan.popular
                        ? 'bg-cyan-500 text-[#030810] hover:bg-cyan-400'
                        : 'bg-white/5 text-white hover:bg-white/10 border border-white/10'
                    }`}
                    data-testid={`select-${plan.name.toLowerCase()}`}
                  >
                    Contact Sales
                  </button> */
                  null
                ) : (
                  <button
                    onClick={() => handleUpgrade(plan.name)}
                    className={`w-full py-3 rounded-lg font-medium text-sm transition-all ${
                      plan.popular
                        ? 'bg-cyan-500 text-[#030810] hover:bg-cyan-400'
                        : 'bg-white/5 text-white hover:bg-white/10 border border-white/10'
                    }`}
                    data-testid={`select-${plan.name.toLowerCase()}`}
                  >
                    Select Plan
                  </button>
                )}
              </motion.div>
            ))}
          </div>

          {/* Current Subscription */}
          {user.plan !== 'Trial' && (
            <div className="mt-12">
              <h2 className="text-xl font-semibold mb-4">Current Subscription</h2>
              <div className="glass-panel p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-lg font-medium">{user.plan} Plan</p>
                    <p className="text-white/40 text-sm">Next billing date: January 15, 2025</p>
                  </div>
                  <button className="text-cyan-400 text-sm hover:text-cyan-300">
                    Manage subscription
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* FAQ */}
          <div className="mt-16 text-center">
            <p className="text-white/40 text-sm">
              Have questions?{' '}
              <Link to="/faq" className="text-cyan-400 hover:text-cyan-300">
                Read our FAQ
              </Link>
              {' '}or{' '}
              {/* <Link to="/demo" className="text-cyan-400 hover:text-cyan-300">
                contact sales
              </Link> */}
            </p>
          </div>
        </main>
      </div>
    </div>
  );
};

export default BillingPage;
